import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type {
  ActionIA,
  BrouillonItem,
  ChangementItem,
  Item,
  MembrePublic,
  PropositionIA,
} from '@camellie/shared';
import { api, surDeconnexion } from './api/client';
import { magasin } from './temps-reel/magasin';
import { EcranConnexion } from './ecrans/EcranConnexion';
import { EcranBrief } from './ecrans/EcranBrief';
import { EcranAgenda } from './ecrans/EcranAgenda';
import { EcranEnfants } from './ecrans/EcranEnfants';
import { EcranCourses } from './ecrans/EcranCourses';
import { Confirmation } from './composants/Confirmation';

type Session = { etat: 'chargement' } | { etat: 'anonyme' } | { etat: 'ouverte'; membre: MembrePublic; famille_id: string };

type Onglet = 'accueil' | 'agenda' | 'enfants' | 'courses';

export function App() {
  const [session, setSession] = useState<Session>({ etat: 'chargement' });
  const [membres, setMembres] = useState<MembrePublic[]>([]);
  const [onglet, setOnglet] = useState<Onglet>('accueil');
  const [deconnexion, setDeconnexion] = useState(false);
  const [horsLigne, setHorsLigne] = useState(false);
  const [lot, setLot] = useState<{ resume: string; ids: string[] } | null>(null);
  const minuterieLot = useRef<number | null>(null);

  /**
   * Vue personnelle ou vue du foyer. Le choix est propre a l'appareil, pas au
   * compte : il vit dans localStorage, qui peut etre vide ou inaccessible
   * (navigation privee, donnees de site effacees), d'ou le try/catch.
   */
  const [vueTout, setVueTout] = useState(() => {
    try {
      return localStorage.getItem('camellie:vue') === 'tout';
    } catch {
      return false;
    }
  });

  const changerVue = useCallback((v: boolean) => {
    setVueTout(v);
    try {
      localStorage.setItem('camellie:vue', v ? 'tout' : 'moi');
    } catch {
      // Sans persistance on retombe sur la vue personnelle au prochain
      // demarrage : une gene, pas une panne.
    }
  }, []);

  const instantane = useSyncExternalStore(magasin.abonner, magasin.instantane);

  // Reprise de session : le cookie httpOnly survit aux redemarrages, donc
  // dans la vie courante on n'affiche jamais l'ecran de connexion.
  useEffect(() => {
    api
      .moi()
      .then((r) => setSession({ etat: 'ouverte', membre: r.membre, famille_id: r.famille_id }))
      .catch(() => setSession({ etat: 'anonyme' }));
  }, []);

  // Un 401 sur n'importe quel appel renvoie a la connexion, sans attendre que
  // l'utilisateur tombe sur un ecran vide.
  useEffect(
    () =>
      surDeconnexion(() => {
        magasin.arreter();
        setSession({ etat: 'anonyme' });
      }),
    [],
  );

  useEffect(() => {
    if (session.etat !== 'ouverte') return;
    magasin.demarrer();
    api
      .membres()
      .then((r) => setMembres(r.membres))
      .catch(() => undefined);
    return () => magasin.arreter();
  }, [session.etat]);

  const basculer = useCallback((id: string) => {
    // Retour haptique la ou il existe. Safari iOS ne gere pas l'API Vibration :
    // l'appel est sans effet la-bas, le retour visuel de la case suffit.
    navigator.vibrate?.(8);
    magasin.basculer(id);
  }, []);

  const ajouter = useCallback(
    // Le second argument remonte l'item une fois confirme par le serveur :
    // l'assistant en a besoin pour pouvoir annuler tout un lot.
    (brouillon: BrouillonItem, surCree?: (item: Item) => void) => magasin.creer(brouillon, surCree),
    [],
  );

  const supprimer = useCallback((id: string) => magasin.supprimer(id), []);

  const modifier = useCallback(
    (id: string, champs: ChangementItem) => magasin.modifier(id, champs),
    [],
  );

  /**
   * Cree tout ce que l'assistant a compris, sans confirmation prealable, et
   * propose une annulation pendant huit secondes.
   *
   * Confirmer avant coup couterait deux tapes a chaque phrase ; annuler apres
   * n'en coute aucune quand le modele a vu juste, ce qui est le cas courant.
   * Le bandeau vit ici et non dans un ecran : apres avoir dicte des courses on
   * va souvent verifier l'onglet Courses, et l'annulation doit survivre au
   * changement d'onglet.
   *
   * Rend de quoi annuler ce lot-la precisement. En conversation vocale la
   * feuille reste ouverte, le bandeau est donc cache sous son voile, et
   * l'annulation s'offre sur le tour de parole concerne. Le rappel se referme
   * sur ses propres identifiants : deux phrases dictees coup sur coup font
   * deux lots, et annuler le premier ne doit pas emporter le second.
   */
  const creerLot = useCallback((propositions: PropositionIA[]) => {
    const ids: string[] = [];
    for (const p of propositions) {
      const payload: Record<string, unknown> = {};
      if (p.lieu) payload.lieu = p.lieu;
      if (p.pour) payload.pour = p.pour;
      // Les identifiants n'existent qu'apres confirmation du serveur, d'ou la
      // collecte par rappel.
      magasin.creer(
        { type: p.type, titre: p.titre, assigne_a: p.assigne_a, du_le: p.du_le, payload },
        (item) => ids.push(item.id),
      );
    }
    const n = propositions.length;
    setLot({ resume: String(n) + (n > 1 ? ' elements ajoutes' : ' element ajoute'), ids });
    if (minuterieLot.current !== null) window.clearTimeout(minuterieLot.current);
    minuterieLot.current = window.setTimeout(() => setLot(null), 8000);

    return () => {
      for (const id of ids) magasin.supprimer(id);
      // Retire le bandeau s'il porte encore ce lot : sans cela il proposerait
      // d'annuler une seconde fois ce qui vient de l'etre.
      setLot((l) => (l !== null && l.ids === ids ? null : l));
    };
  }, []);

  /**
   * Applique ce que l'assistant veut faire a des elements existants, et rend
   * de quoi revenir en arriere.
   *
   * L'annulation retient l'etat d'avant plutot que l'inverse du geste : une
   * suppression ne se defait pas, elle se recree. L'item revient avec un
   * nouvel identifiant — ce qui suffit ici, puisque personne ne garde un
   * identifiant en tete, alors qu'un rendez-vous efface par erreur ne se
   * retrouve nulle part.
   */
  const agirLot = useCallback((actions: ActionIA[]) => {
    const retours: (() => void)[] = [];

    for (const a of actions) {
      const avant = magasin.instantane().items.find((i) => i.id === a.id);
      if (!avant) continue;

      if (a.quoi === 'supprimer') {
        magasin.supprimer(a.id);
        retours.push(() =>
          magasin.creer({
            type: avant.type,
            titre: avant.titre,
            assigne_a: avant.assigne_a,
            du_le: avant.du_le,
            payload: (avant.payload ?? {}) as Record<string, unknown>,
          }),
        );
        continue;
      }

      if (a.quoi === 'deplacer') {
        magasin.modifier(a.id, { du_le: a.du_le });
        retours.push(() => magasin.modifier(a.id, { du_le: avant.du_le }));
        continue;
      }

      // `fait` / `a_faire` : on ne bascule que si l'etat differe deja, sinon
      // « j ai achete le lait » dit deux fois le decocherait.
      const voulu = a.quoi === 'fait' ? 'fait' : 'a_faire';
      if (avant.statut === voulu) continue;
      magasin.basculer(a.id);
      retours.push(() => magasin.basculer(a.id));
    }

    const n = retours.length;
    if (n > 0) {
      setLot({ resume: n > 1 ? String(n) + ' changements' : '1 changement', ids: [] });
      if (minuterieLot.current !== null) window.clearTimeout(minuterieLot.current);
      minuterieLot.current = window.setTimeout(() => setLot(null), 8000);
    }

    return () => {
      for (const revenir of retours) revenir();
      setLot(null);
    };
  }, []);

  const annulerLot = useCallback(() => {
    if (minuterieLot.current !== null) window.clearTimeout(minuterieLot.current);
    setLot((l) => {
      for (const id of l?.ids ?? []) magasin.supprimer(id);
      return null;
    });
  }, []);

  const deconnecter = useCallback(() => {
    void api.deconnexion().catch(() => undefined);
    magasin.arreter();
    setDeconnexion(false);
    setSession({ etat: 'anonyme' });
  }, []);

  // Une coupure de quelques secondes en changeant d'antenne ne merite pas
  // d'alerte : on n'affiche le bandeau que si elle dure.
  useEffect(() => {
    if (instantane.connecte) {
      setHorsLigne(false);
      return;
    }
    const t = window.setTimeout(() => setHorsLigne(true), 4000);
    return () => window.clearTimeout(t);
  }, [instantane.connecte]);

  if (session.etat === 'chargement') {
    return <div className="connexion" />;
  }

  if (session.etat === 'anonyme') {
    return (
      <EcranConnexion
        onConnecte={(membre, famille_id) => setSession({ etat: 'ouverte', membre, famille_id })}
      />
    );
  }

  return (
    <div className="app">
      {horsLigne && (
        <div className="hors-ligne" role="status">
          Hors ligne — les changements partiront au retour du reseau
        </div>
      )}

      {onglet === 'accueil' && (
        <EcranBrief
          items={instantane.items}
          membres={membres}
          moi={session.membre}
          famille_id={session.famille_id}
          vueTout={vueTout}
          onVueTout={changerVue}
          onBasculer={basculer}
          connecte={instantane.connecte}
          onDeconnecter={() => setDeconnexion(true)}
          onAjouter={ajouter}
          onLotAssistant={creerLot}
          onAgirAssistant={agirLot}
          onModifier={modifier}
          onSupprimer={supprimer}
        />
      )}

      {onglet === 'agenda' && (
        <EcranAgenda
          items={instantane.items}
          membres={membres}
          moi={session.membre}
          vueTout={vueTout}
          onVueTout={changerVue}
          onBasculer={basculer}
          onAjouter={ajouter}
          onModifier={modifier}
          onSupprimer={supprimer}
        />
      )}

      {onglet === 'enfants' && (
        <EcranEnfants
          items={instantane.items}
          membres={membres}
          moi={session.membre}
          onBasculer={basculer}
          onAjouter={ajouter}
          onModifier={modifier}
          onSupprimer={supprimer}
        />
      )}

      {onglet === 'courses' && (
        <EcranCourses
          items={instantane.items}
          onBasculer={basculer}
          onAjouter={ajouter}
          onSupprimer={supprimer}
        />
      )}

      {lot && (
        <div className="bandeau-lot" role="status">
          {lot.resume}
          <button type="button" onClick={annulerLot}>
            Annuler
          </button>
        </div>
      )}

      {instantane.incident && (
        <div className="incident" role="alert">
          {instantane.incident}
        </div>
      )}

      <nav className="nav">
        <button
          type="button"
          onClick={() => setOnglet('accueil')}
          aria-current={onglet === 'accueil' ? 'page' : undefined}
        >
          <span className="glyphe" aria-hidden="true">
            &#9788;
          </span>
          Accueil
        </button>
        <button
          type="button"
          onClick={() => setOnglet('agenda')}
          aria-current={onglet === 'agenda' ? 'page' : undefined}
        >
          <span className="glyphe" aria-hidden="true">
            &#128197;
          </span>
          Agenda
        </button>
        <button
          type="button"
          onClick={() => setOnglet('enfants')}
          aria-current={onglet === 'enfants' ? 'page' : undefined}
        >
          <span className="glyphe" aria-hidden="true">
            &#128118;
          </span>
          Enfants
        </button>
        <button
          type="button"
          onClick={() => setOnglet('courses')}
          aria-current={onglet === 'courses' ? 'page' : undefined}
        >
          <span className="glyphe" aria-hidden="true">
            &#128722;
          </span>
          Courses
        </button>
      </nav>

      {deconnexion && (
        <Confirmation
          message={'Se deconnecter de CamEllie ?'}
          action="Se deconnecter"
          onConfirmer={deconnecter}
          onAnnuler={() => setDeconnexion(false)}
        />
      )}
    </div>
  );
}
